import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { Contact, PhoneNumber } from '../types/common';
import type { Address } from '../types/address';
import type { Household } from '../types/household';

const APP_DATE_LONGTIME_FORMAT = 'DD.MM.YYYY HH:mm:ss';
const APP_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';
const APP_DATE_FORMAT = 'DD.MM.YYYY';
const APP_DATE_SHORT_FORMAT = 'DDMMYY';
const API_DATE_FORMAT = 'YYYY-MM-DD';
const VAT = 19;

dayjs.extend(customParseFormat);

function formatFullFormOfAddress(household: Household) {
  return `${household.salutation} ${household.firstName} ${household.lastName}`;
}

function formatFullName({ firstName, lastName }: Pick<Household, 'firstName' | 'lastName'>) {
  return `${firstName} ${lastName}`;
}

function formatDateFullTime(date: string) {
  return (date ? dayjs(date).format(APP_DATE_LONGTIME_FORMAT) : null);
}
const formatDateTime = (date: string) => (date ? dayjs(date).format(APP_DATETIME_FORMAT) : null);
const formatDate = (date: string) => (date ? dayjs(date).format(APP_DATE_FORMAT) : null);
const formatDateShort = (date: string) => (date ? dayjs(date).format(APP_DATE_SHORT_FORMAT) : null);
const formatDateApiFormat = (date: string) => (date ? dayjs(date).format(API_DATE_FORMAT) : null);

function formatPrice(price: number) {
  return Number(price).toLocaleString('de-DE', { minimumFractionDigits: 2 });
}
function formatPriceWithCurrency(price: number) {
  return `${Number(price).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`;
}
function priceWithoutVat(price: number) {
  return Number(price / (1 + VAT / 100)).toFixed(2);
}

const formatPhoneNumber = (tel: PhoneNumber) => `${tel.prefix} ${tel.number}`;

function addressAsText(address: Address) {
  return `${`${address.street} ${address.houseNumber} ${address.suffix ?? ''}`.trim()}, ${address.zipCode} ${address.city}`;
}

function formatFullHouseNumber({ houseNumber, suffix }: Pick<Address, 'houseNumber' | 'suffix'>) {
  return `${houseNumber ?? ''}${suffix ?? ''}`.trim();
}

function formatAddress(address: Address) {
  const {
    street,
    zipCode,
    city,
  } = address;
  return `${street} ${formatFullHouseNumber(address)}, ${zipCode} ${city}`;
}

const booleanAsText = (boolean: boolean) => (boolean ? 'Ja' : 'Nein');

function getFormOfAddress({ isCompany, salutation }: Pick<Household, 'isCompany' | 'salutation'>): string {
  return isCompany ? '/Firma' : `/${salutation}`;
}

function getCompanyOrFullName({
  isCompany,
  companyName,
  firstName,
  lastName,
}: Household): string {
  return isCompany ? companyName! : formatFullName({ firstName, lastName });
}

function getLandlineOrMobileNumber(contact?: Contact | null): string {
  let result = '';

  if (contact?.tel?.prefix) {
    result = formatPhoneNumber(contact.tel);
  } else if (contact?.mobile?.prefix) {
    result = formatPhoneNumber(contact.mobile);
  }

  return result;
}

export {
  APP_DATETIME_FORMAT,
  APP_DATE_FORMAT,
  APP_DATE_SHORT_FORMAT,
  API_DATE_FORMAT,
  VAT,
  dayjs,
  formatAddress,
  formatFullFormOfAddress,
  formatFullName,
  formatPrice,
  formatPriceWithCurrency,
  formatDate,
  formatPhoneNumber,
  getCompanyOrFullName,
  getFormOfAddress,
  getLandlineOrMobileNumber,
  priceWithoutVat,
  formatDateFullTime,
  formatDateTime,
  formatDateShort,
  formatDateApiFormat,
  formatFullHouseNumber,
  addressAsText,
  booleanAsText,
};
